import {keys} from 'lodash';
import {
  compact,
  eq,
  flow,
  get,
  isEmpty,
  isNil,
  join,
  negate,
  size,
} from 'lodash/fp';
import {createSelector} from 'reselect';

import {pageSize} from '/constant/data.constant';
import {ALERTS_STATE_IS_LOADING} from '/enum/alertsState.enum';
import {
  PAGE_NUMBER_FIELD_NAME,
  PAGE_SIZE_FIELD_NAME,
} from '/enum/apiFields.enum';
import {CLIENT_JOURNEY_STATE_IS_LOADING} from '/enum/clientJourneyState.enum';
import {
  FIRST_WEEK,
  PRE_JOURNEY,
  WEEKLY_CHECK_INS,
} from '/enum/clientJourneyTasks.enum';
import {
  ORDER_STATE_IS_LOADING,
  ORDERS_STATE_IS_LOADING,
} from '/enum/ordersState.enum';
import {DIRECTION_DESC} from '/enum/sort.enum';
import {calculatePageNumber} from '/util/requestParams.util';
import {fromProps, withProps} from '/util/selector.util';

import {client as clientState} from './root.selector';

export const allProfiles = createSelector(clientState, get('allProfiles'));

export const allOrders = createSelector(clientState, get('allOrders'));

export const allOrdersMeta = createSelector(clientState, get('allOrdersMeta'));

export const allOrdersState = createSelector(
  clientState,
  get('allOrdersState'),
);

export const allOrderDetail = createSelector(
  clientState,
  get('allOrderDetail'),
);

export const allOrderDetailState = createSelector(
  clientState,
  get('allOrderDetailState'),
);

export const profileById = createSelector(
  fromProps(get('clientId')),
  allProfiles,
  (clientId, profiles) => get(clientId)(profiles),
);

export const hasProfileById = createSelector(profileById, negate(isNil));

export const isCoachById = createSelector(
  profileById,
  flow([get('customerTypeId'), eq(1)]),
);

export const activatedOnById = createSelector(profileById, get('activatedOn'));

export const avatarUrlById = createSelector(profileById, get('avatarUrl'));

export const firstOrderId = createSelector(profileById, get('firstOrderId'));

export const accountEmailById = createSelector(
  profileById,
  get('emailAddress'),
);

export const phoneNumberById = createSelector(profileById, get('phoneNumber'));

export const firstNameById = createSelector(profileById, get('firstName'));

export const lastNameById = createSelector(profileById, get('lastName'));

export const urlDetailsById = createSelector(profileById, get('urlDetails'));

export const fullNameById = createSelector(
  firstNameById,
  lastNameById,
  (firstName, lastName) => flow([compact, join(' ')])([firstName, lastName]),
);

export const coachById = createSelector(profileById, get('coach'));

export const coachIdById = createSelector(coachById, get('id'));

export const coachFirstNameById = createSelector(coachById, get('firstName'));

export const coachLastNameById = createSelector(coachById, get('lastName'));

export const coachFullNameById = createSelector(
  coachFirstNameById,
  coachLastNameById,
  (firstName, lastName) => flow([compact, join(' ')])([firstName, lastName]),
);

export const addressById = createSelector(profileById, get('address'));

export const addressCityById = createSelector(addressById, get('city'));

export const addressStateById = createSelector(addressById, get('state'));

export const addressCityStateById = createSelector(
  addressCityById,
  addressStateById,
  (city, state) => {
    return flow([compact, join(', ')])([city, state]);
  },
);

export const nextScheduledPremierOrderById = createSelector(
  profileById,
  get('nextScheduledPremierOrder'),
);

export const hasScheduledPremierOrderById = createSelector(
  nextScheduledPremierOrderById,
  negate(isEmpty),
);

export const nextPremierOrderScheduledOnById = createSelector(
  nextScheduledPremierOrderById,
  get('scheduledOn'),
);

export const nextPremierOrderCvById = createSelector(
  nextScheduledPremierOrderById,
  get('cv'),
);

export const nextPremierOrderQvById = createSelector(
  nextScheduledPremierOrderById,
  get('qv'),
);

export const nextPremierOrderTotalById = createSelector(
  nextScheduledPremierOrderById,
  flow([get('price.total'), parseFloat]),
);

export const nextPremierOrderCurrencyCodeById = createSelector(
  nextScheduledPremierOrderById,
  get('price.currencyCode'),
);

export const nextPremierOrderAutoShipStatusById = createSelector(
  nextScheduledPremierOrderById,
  get('autoshipStatus'),
);

export const wellnessCreditsById = createSelector(
  profileById,
  get('wellnessCredits'),
);

export const wellnessCreditsBalanceById = createSelector(
  wellnessCreditsById,
  flow([get('balance'), parseInt]),
);

export const wellnessCreditsCurrencyCodeById = createSelector(
  wellnessCreditsById,
  get('currencyCode'),
);

export const rewardsById = createSelector(profileById, get('rewards'));

export const rewardsBalanceById = createSelector(
  rewardsById,
  flow([get('balance'), parseFloat]),
);

export const rewardsCurrencyCodeById = createSelector(
  rewardsById,
  get('currencyCode'),
);

export const ordersById = createSelector(
  fromProps(get('clientId')),
  allOrders,
  (clientId, orders) => get(clientId)(orders),
);

export const profileOrdersById = createSelector(
  fromProps(get('clientId')),
  allOrdersMeta,
  allOrders,
  (clientId, allMeta, allOrdersData) =>
    get(`${clientId}.profileOrders`)(allMeta) || get(clientId)(allOrdersData),
);

export const ordersCountById = createSelector(ordersById, size);

export const hasOrdersById = createSelector(ordersById, negate(isNil));

export const ordersStateValue = createSelector(
  fromProps(get('clientId')),
  fromProps(get('stateName')),
  allOrdersState,
  (clientId, stateName, allState) => get(`${clientId}.${stateName}`)(allState),
);

export const ordersIsLoadingStateValue = withProps({
  stateName: ORDERS_STATE_IS_LOADING,
})(ordersStateValue);

export const ordersMeta = createSelector(
  fromProps(get('clientId')),
  allOrdersMeta,
  (clientId, allMeta) => get(clientId)(allMeta),
);

export const ordersSortParams = createSelector([], () => ({
  sortBy: 'orderDate',
  sortOrder: DIRECTION_DESC,
}));

export const ordersPaginationParamsById = createSelector(
  ordersCountById,
  ordersMeta,
  (ordersCount, meta) => {
    const total = meta?.page?.total ?? meta?.report?.total;
    return {
      [PAGE_SIZE_FIELD_NAME]: pageSize,
      [PAGE_NUMBER_FIELD_NAME]: calculatePageNumber({
        numberOfRecords: ordersCount,
        pageSize,
        total,
      }),
    };
  },
);

export const orderById = createSelector(
  fromProps(get('orderId')),
  allOrderDetail,
  (orderId, orderDetail) => get(orderId)(orderDetail),
);

export const hasOrderById = createSelector(orderById, negate(isNil));

export const orderCustomerIdById = createSelector(
  orderById,
  get('customer.id'),
);
export const orderCustomerTypeIdById = createSelector(
  orderById,
  get('customer.customerTypeId'),
);
export const orderNumberById = createSelector(orderById, get('orderNumber'));

const getPackages = (order: any) => {
  const packages = get('packages')(order);
  return Array.isArray(packages) ? packages : [];
};
const getPackageTrackingNumber = (pkg: any) => get('trackingNumber')(pkg);
const getPackageTrackingUrl = (pkg: any) => get('trackingUrl')(pkg);

const getTrackingNumberFromPackages = (order: any) => {
  const packages = getPackages(order);
  const firstTrackedPackage = packages.find((pkg: any) =>
    Boolean(getPackageTrackingNumber(pkg)),
  );

  return firstTrackedPackage
    ? getPackageTrackingNumber(firstTrackedPackage)
    : '';
};

const getTrackingUrlFromPackages = (order: any) => {
  const packages = getPackages(order);
  const trackingNumber = get('trackingNumber')(order);

  if (trackingNumber) {
    const matchingPackage = packages.find(
      (pkg: any) =>
        getPackageTrackingNumber(pkg) === trackingNumber &&
        Boolean(getPackageTrackingUrl(pkg)),
    );
    if (matchingPackage) {
      return getPackageTrackingUrl(matchingPackage);
    }
  }

  const firstTrackablePackage = packages.find((pkg: any) =>
    Boolean(getPackageTrackingUrl(pkg)),
  );
  return firstTrackablePackage
    ? getPackageTrackingUrl(firstTrackablePackage)
    : '';
};

export const orderTrackingNumberById = createSelector(
  orderById,
  order => get('trackingNumber')(order) || getTrackingNumberFromPackages(order),
);
export const orderTrackingUrlById = createSelector(
  orderById,
  getTrackingUrlFromPackages,
);
export const orderCustomerActivatedOnDate = createSelector(
  orderById,
  get('customer.activatedOn'),
);

export const orderBillingAddressById = createSelector(
  orderById,
  get('billingAddress'),
);

export const orderShippingAddressById = createSelector(
  orderById,
  get('shippingAddress'),
);

export const orderItemsById = createSelector(orderById, get('items'));

export const orderCvById = createSelector(orderById, get('cv'));

export const orderPackagesById = createSelector(orderById, get('packages'));

export const orderQvById = createSelector(orderById, get('qv'));

export const orderTypeById = createSelector(orderById, get('orderType'));

export const orderDateById = createSelector(orderById, get('orderedOn'));

export const orderPriceById = createSelector(orderById, get('price'));

export const orderCurrencyCodeById = createSelector(
  orderPriceById,
  get('currencyCode'),
);

export const orderShippingPriceById = createSelector(
  orderPriceById,
  flow([get('shippingPrice'), parseFloat]),
);

export const orderSubtotalById = createSelector(
  orderPriceById,
  flow([get('subtotal'), parseFloat]),
);

export const orderSubtotalConsumableById = createSelector(
  orderById,
  flow([get('subTotalConsumable'), parseFloat]),
);

export const orderTaxById = createSelector(
  orderPriceById,
  flow([get('tax'), parseFloat]),
);

export const orderTotalById = createSelector(
  orderPriceById,
  flow([get('total'), parseFloat]),
);

export const orderStatusById = createSelector(orderById, get('orderStatus'));

export const orderStateValue = createSelector(
  fromProps(get('orderId')),
  fromProps(get('stateName')),
  allOrderDetailState,
  (orderId, stateName, allState) => get(`${orderId}.${stateName}`)(allState),
);

export const orderIsLoadingStateValue = withProps({
  stateName: ORDER_STATE_IS_LOADING,
})(orderStateValue);

export const allAlertsData = createSelector(clientState, get('allAlertsData'));

export const allAlertsById = createSelector(
  fromProps(get('clientId')),
  allAlertsData,
  (clientId, alerts) => get(clientId)(alerts),
);

export const allAlertsState = createSelector(
  clientState,
  get('allAlertsState'),
);

export const hasAllAlertsById = createSelector(allAlertsById, negate(isNil));

export const isAlertsLoading = createSelector(allAlertsState, alerts => {
  const alertsTypes = keys(alerts);
  let result = false;
  alertsTypes.forEach((alert: any) => {
    result = result || alerts[alert][ALERTS_STATE_IS_LOADING];
  });
  return result;
});

export const clientJourneyTasks = createSelector(
  clientState,
  get('clientJourneyTasks'),
);

export const allTasks = createSelector(clientJourneyTasks, get('allTasks'));

// to check whether the data has been loaded
export const hasAllTasks = createSelector(allTasks, negate(isNil));

// to check whether we got data in the API response
export const isEmptyAllTasks = createSelector(allTasks, isEmpty);

export const clientJourneyTasksById = createSelector(
  fromProps(get('clientId')),
  clientJourneyTasks,
  (clientId, tasks) => get(`${clientId}`)(tasks),
);

export const firstWeekTasksById = createSelector(
  clientJourneyTasksById,
  get(FIRST_WEEK),
);

export const preJourneyTasksById = createSelector(
  clientJourneyTasksById,
  get(PRE_JOURNEY),
);

export const weeklyCheckInsTasksById = createSelector(
  clientJourneyTasksById,
  get(WEEKLY_CHECK_INS),
);

export const lastWeeklyCheckInDate = createSelector(
  weeklyCheckInsTasksById,
  get('[0].lastweekCheckInDate'),
);

export const currentWeeklyCheckInDate = createSelector(
  weeklyCheckInsTasksById,
  get('[0].currentCheckInDate'),
);

export const clientJourneyTasksByType = createSelector(
  fromProps(get('type')),
  clientJourneyTasksById,
  (type, tasks) => get(`${type}`)(tasks),
);

export const filteredClientJourneyTasks = createSelector(
  fromProps(get('filter')),
  allTasks,
  (filter, tasks) => tasks.filter((task: any) => task.screenTabCode === filter),
);

export const hasFirstWeekTasks = createSelector(
  firstWeekTasksById,
  negate(isNil),
);

export const hasPreJourneyTasks = createSelector(
  preJourneyTasksById,
  negate(isNil),
);
export const hasWeeklyCheckInsTasks = createSelector(
  weeklyCheckInsTasksById,
  negate(isNil),
);

export const firstOrderDetail = createSelector(
  clientState,
  get('firstOrderDetail'),
);

export const firstOrderById = createSelector(
  fromProps(get('orderId')),
  firstOrderDetail,
  (orderId, orderDetail) => get(orderId)(orderDetail),
);

export const hasFirstOrderById = createSelector(firstOrderById, negate(isNil));

export const firstOrderShippingStatusById = createSelector(
  firstOrderById,
  get('orderStatus'),
);
export const firstOrderPackagesById = createSelector(
  firstOrderById,
  get('packages'),
);

export const allClientJourneyState = createSelector(
  clientState,
  get('allClientJourneyState'),
);

export const isClientJourneyLoading = createSelector(
  allClientJourneyState,
  CRMTasks => {
    const CRMTaskTypes = keys(CRMTasks);
    let result = false;
    CRMTaskTypes.forEach((task: any) => {
      result = result || CRMTasks[task][CLIENT_JOURNEY_STATE_IS_LOADING];
    });
    return result;
  },
);
export const notesById = createSelector(profileById, get('notes'));

export const notesUpdatedTsById = createSelector(
  profileById,
  get('notesUpdatedTs'),
);
export const allNotesState = createSelector(clientState, get('allNotesState'));
export const mwlData = createSelector(clientState, get('allMwlData'));
export const mswlData = createSelector(clientState, get('allMswlData'));
export const weightGraphData = createSelector(
  clientState,
  get('allWeightGraphData'),
);
export const bodyMeasurementsData = createSelector(
  clientState,
  get('bodyMeasurementsData'),
);
export const clientStartDate = createSelector(clientState, get('startDate'));

export const clientPlanDetails = createSelector(
  clientState,
  get('planDetails'),
);
export const clientmyWhy = createSelector(clientState, get('myWhy'));

export const coachAlias = createSelector(clientState, get('coachAlias'));

export const coachAliasById = createSelector(
  fromProps(get('coachId')),
  coachAlias,
  (clientId, coachAliasObj) => get(clientId)(coachAliasObj),
);

export const hasCoachAliasById = createSelector(coachAliasById, alias =>
  Boolean(alias),
);
